import React, { useState, useEffect } from "react";
import Contact from "../components/contact";
import Footer from "../components/Footer";
import StarRating from "../components/StarRating";
import { testimonials } from "../data/testimonials";
import {
  ChevronLeft,
  ChevronRight,
  User,
  Building,
  Users,
  Globe,
  ArrowRight,
  Home,
  Award,
  MapPin,
  Heart,
} from "lucide-react";
import CountUp from "react-countup";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const ResidentialDesign = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentProject, setCurrentProject] = useState(0);
  const [showFullDescription, setShowFullDescription] = useState(false);

  // Track the active image index for the currently selected project's thumbnail view
  const [currentProjectImageIndex, setCurrentProjectImageIndex] = useState(0);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const heroImages = [
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1779700360/Bharil_Ji_living_area_1_j20mvp.jpg",
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1779700356/Img_6_vhfzye.jpg",
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1779700355/hall_2_khtyct.jpg",
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1779700341/DSC_1742-HDR_1_jjuos2.jpg",
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1779700264/Img_3_ujbd1v.jpg",
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1779700262/Img_3_Bedroom_cwchkl.png",
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1779700263/img204_d5gmgk.jpg",
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1779700262/FIRST_FLOOR_1_xjks7g.jpg",
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1779700262/Kuldeep_Ji_jizwgq.jpg",
  ];

  // ---------------- FETCH RESIDENTIAL PROJECTS ----------------
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await fetch("https://archpoint.onrender.com/api/residential");
        const data = await res.json();
        setProjects(data);
      } catch (err) {
        console.error("Error fetching residential projects", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  // Reset thumbnail preview index whenever the main project changes
  useEffect(() => {
    setCurrentProjectImageIndex(0);
  }, [currentProject]);

  // Arch Point Advantages
  const advantages = [
    { icon: Building, number: "300+", label: "Projects" },
    { icon: MapPin, number: "10+", label: "Cities" },
    { icon: Users, number: "50+", label: "Team" },
    { icon: Award, number: "ISO", label: "Certified" },
    { icon: Globe, number: "Govt.", label: "Recognised" },
    { icon: Heart, number: "Quantum", label: "Architecture" },
  ];

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  const splitNumber = (numStr) => {
    const match = numStr.match(/^(\d+)(.*)$/);
    return match ? { value: parseInt(match[1]), suffix: match[2] } : null;
  };

  // What we offer (service cards)
  const designServices = [
    {
      icon: Home,
      title: "Dream Home Planning",
      description:
        "Complete architectural planning to bring your dream home to life",
    },
    {
      icon: Building,
      title: "3D Visualization",
      description: "Realistic 3D renders to help you visualize your future home",
    },
    {
      icon: Award,
      title: "Premium Quality",
      description: "High-quality design standards ensuring lasting beauty",
    },
    {
      icon: Heart,
      title: "Personalized Design",
      description: "Every design reflects your personality and lifestyle needs",
    },
  ];

  // Auto-slide hero carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () =>
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  const prevSlide = () =>
    setCurrentSlide(
      (prev) => (prev - 1 + heroImages.length) % heroImages.length
    );
  const nextProject = () =>
    setCurrentProject((prev) => (prev + 1) % projects.length);
  const prevProject = () =>
    setCurrentProject((prev) => (prev - 1 + projects.length) % projects.length);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Carousel Section */}
      <section className="relative h-screen overflow-hidden">
        <div className="relative w-full h-full">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                index === currentSlide ? "opacity-100" : "opacity-0"
              }`}
            >
              <img
                src={image}
                alt={`Residential Hero ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black bg-opacity-40"></div>
            </div>
          ))}
        </div>

        <div className="absolute inset-0 flex items-center justify-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white text-center tracking-wide">
            <span className="text-yellow-400">Residential</span>
            <br />
            Design
          </h1>
        </div>

        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-yellow-400 transition-colors duration-300 z-10"
        >
          <ChevronLeft size={48} />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-yellow-400 transition-colors duration-300 z-10"
        >
          <ChevronRight size={48} />
        </button>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-colors duration-300 ${
                index === currentSlide
                  ? "bg-yellow-400"
                  : "bg-white bg-opacity-50"
              }`}
            />
          ))}
        </div>
      </section>

      {/* ---------------- OUR SERVICES ---------------- */}
      <section className="py-20 px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-800">
                Our <span className="text-yellow-500">Services</span>
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                We design the best home architecture plan for you to live your
                dreams.
              </p>

              {showFullDescription && (
                <div className="text-gray-600 leading-relaxed mb-6">
                  <p className="mb-4">
                    Having a beautiful home or residence is one of the most
                    important dreams of everyone. Everyone in this world wishes
                    for a perfect residence with everything precisely designed
                    and decorated. And therefore Arch Point design the best home
                    architecture plan for you to live your dreams.
                  </p>
                  <p className="mb-4">
                    Our residential design approach combines functionality with
                    aesthetic appeal, creating spaces that not only look
                    beautiful but also enhance your daily living experience.
                    Every project is unique, reflecting the personality and
                    lifestyle of its inhabitants.
                  </p>
                </div>
              )}

              <button
                onClick={() => setShowFullDescription(!showFullDescription)}
                className="inline-flex items-center px-6 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors duration-300 font-semibold"
              >
                {showFullDescription ? "Read Less" : "Read More"}
                <ArrowRight className="ml-2" size={20} />
              </button>
            </div>

            <Contact />
          </div>
        </div>
      </section>

      {/* What We Offer (cards) */}
      <section className="py-20 px-4 md:px-8 lg:px-16 bg-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-800">
            What We <span className="text-yellow-500">Offer</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {designServices.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 shadow-xl text-center hover:shadow-2xl transition-shadow duration-300"
                >
                  <div className="bg-yellow-500 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                    <IconComponent size={28} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">
                    {service.title}
                  </h3>
                  <p className="text-gray-600">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our USP Section */}
      <section className="py-20 px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-800">
                Our <span className="text-yellow-500">USP</span>
              </h2>
              <h3 className="text-2xl font-semibold mb-4 text-gray-700">
                Dream Home Realization
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                We specialize in turning your dream home vision into reality
                through innovative design solutions and quantum architecture
                principles. Every residence we design is a perfect blend of
                functionality, aesthetics, and personal expression, ensuring your
                home truly reflects who you are.
              </p>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&h=400&fit=crop"
                alt="Dream Home Design"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-yellow-500 to-transparent opacity-20 rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- ADVANTAGES / MILESTONES ---------------- */}
      <section className="py-20 px-4 md:px-8 lg:px-16 bg-yellow-500" ref={ref}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-extrabold text-center mb-12 text-white">
            Arch Point Advantages
          </h2>

          <div className="grid grid-cols-2 lg:grid-cols-6 gap-6">
            {advantages.map((advantage, index) => {
              const IconComponent = advantage.icon;
              const numberParts = splitNumber(advantage.number);

              return (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="bg-white bg-opacity-20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <IconComponent size={24} className="text-white" />
                  </div>
                  <h3 className="text-3xl font-extrabold text-white mb-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]">
                    {numberParts ? (
                      <>
                        {inView ? (
                          <CountUp end={numberParts.value} duration={2} />
                        ) : (
                          "0"
                        )}
                        {numberParts.suffix}
                      </>
                    ) : (
                      advantage.number
                    )}
                  </h3>
                  <p className="text-white font-bold text-sm tracking-wide">
                    {advantage.label}
                  </p>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-8">
            <p className="text-white font-medium text-lg italic">
              Thanks to all visionaries for believing in Arch Point
            </p>
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      {projects.length > 0 && (
        <section className="py-20 px-4 md:px-8 lg:px-16">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12 text-gray-800">
              Residential <span className="text-yellow-500">Projects</span>
            </h2>

            <div className="relative mb-8">
              <img
                src={
                  projects[currentProject].images[currentProjectImageIndex] ||
                  projects[currentProject].images[0]
                }
                alt={projects[currentProject].name}
                className="w-full h-[500px] object-cover rounded-2xl shadow-xl transition-all duration-300"
              />
              <div className="absolute inset-0 bg-black/30 rounded-2xl pointer-events-none"></div>

              <div className="absolute bottom-6 left-6 pointer-events-none">
                <h3 className="text-3xl font-bold text-white">
                  {projects[currentProject].name}
                </h3>
              </div>

              <button
                onClick={prevProject}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-yellow-400 bg-black/40 hover:bg-black/60 p-2 rounded-full transition"
              >
                <ChevronLeft size={40} />
              </button>
              <button
                onClick={nextProject}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-yellow-400 bg-black/40 hover:bg-black/60 p-2 rounded-full transition"
              >
                <ChevronRight size={40} />
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {projects[currentProject].images.slice(0, 8).map((img, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setCurrentProjectImageIndex(i)}
                  className={`relative overflow-hidden rounded-lg shadow-md transition transform hover:-translate-y-1 focus:outline-none ${
                    currentProjectImageIndex === i
                      ? "ring-4 ring-yellow-500"
                      : "opacity-80 hover:opacity-100"
                  }`}
                >
                  <img
                    src={img}
                    alt={`project-${i}`}
                    className="w-full h-32 object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------------- TESTIMONIALS (shared data) ---------------- */}
      <section className="py-20 px-4 md:px-8 lg:px-16 bg-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-800">
            What Our <span className="text-yellow-500">Clients Say</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-xl">
                <StarRating rating={testimonial.rating} />
                <p className="text-gray-600 mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">
                      {testimonial.name}
                    </h4>
                    <p className="text-gray-500 text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ResidentialDesign;